import { isNull } from "lodash";
import { AnimatePresence } from "motion/react";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTimer } from "react-timer-hook";
import useAppSelector from "@/core/helpers/useAppSelector";
import {
  incrementVehicleFuelAmount,
  newVehicleAmountSelector,
  refuelingTimeSelector,
  selectedFuelTypeSelector,
  setNewVehicleFuelAmount,
  setRefuelingTime,
  setSelectedFuelAmount,
  setVehicleFuelAmount,
  vehicleSelector,
} from "@/domains/gasStation/slice";
import GasStationRefueling from "@/domains/gasStation/components/GasStationRefueling";

export default function GasStationRefuelingContainer({ onClose }: { onClose: () => void }) {
  const selectedFuelType = useAppSelector(selectedFuelTypeSelector);
  const refuelingTime = useAppSelector(refuelingTimeSelector);
  const newVehicleFuelAmount = useAppSelector(newVehicleAmountSelector);
  const vehicle = useAppSelector(vehicleSelector);
  const dispatch = useDispatch();

  const initialFuelRef = useRef<number | null>(null);
  const maxFuelRef = useRef<number | null>(null);
  const deltaRef = useRef<number | null>(null);

  useEffect(() => {
    deltaRef.current = newVehicleFuelAmount;
  }, [newVehicleFuelAmount]);

  const { seconds, restart } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: async () => {
      const base = initialFuelRef.current ?? vehicle?.fuelAmount ?? 0;
      const maxCap = maxFuelRef.current ?? vehicle?.fuelMaxAmount ?? 0;
      const delta = deltaRef.current ?? 0;

      const finalAmount = Math.min(maxCap, base + delta);
      dispatch(setVehicleFuelAmount(finalAmount));

      dispatch(setSelectedFuelAmount(0));
      dispatch(setRefuelingTime(null));
      dispatch(setNewVehicleFuelAmount(null));

      initialFuelRef.current = null;
      maxFuelRef.current = null;
      deltaRef.current = null;

      onClose();
    },
  });

  useEffect(() => {
    if (refuelingTime) {
      restart(refuelingTime);
    }
  }, [restart, refuelingTime]);

  useEffect(() => {
    if (isNull(refuelingTime) || isNull(newVehicleFuelAmount) || !vehicle) return () => {};

    if (initialFuelRef.current === null) {
      initialFuelRef.current = vehicle.fuelAmount;
      maxFuelRef.current = vehicle.fuelMaxAmount;
    }

    const msLeft = refuelingTime.getTime() - Date.now();
    const periods = Math.max(1, Math.floor(msLeft / 500));
    const perPeriod = newVehicleFuelAmount / periods;

    const interval = setInterval(() => {
      dispatch(incrementVehicleFuelAmount(perPeriod));
    }, 500);

    return () => clearInterval(interval);
    // ВАЖНО: убрал vehicle из зависимостей, иначе эффект перезапускается на каждое изменение топлива
  }, [dispatch, refuelingTime, newVehicleFuelAmount]);

  return (
    <AnimatePresence>
      {isNull(refuelingTime) ? null : (
        <GasStationRefueling timer={seconds} fuelType={selectedFuelType} />
      )}
    </AnimatePresence>
  );
}
